import { useEffect } from 'react';
import type { GatePage } from '../types';
import { Blocks } from '../components/Blocks';
import { buildEventVars } from '../lib/stepTwo';
import { prefillTierCtas, resolveUpgradeStepCopy } from '../lib/upgradeStep';

interface Props {
  gatePage: GatePage;
  leadEmail: string | null;
  leadFirstName: string | null;
  eventConfig?: Record<string, unknown>;
  onDecline: () => void;
}

/**
 * The upgrade step between registering and the thank-you (gatePage.upgradeStep).
 * "Your free seat is saved" first and small, then one clear yes per pass and a
 * real "No thank you" button the same size as the yes buttons. Config-driven:
 * no event literals here. Who sees it and why it exists: lib/upgradeStep.ts.
 */
export default function UpgradeStep({ gatePage, leadEmail, leadFirstName, eventConfig, onDecline }: Props) {
  const copy = resolveUpgradeStepCopy(gatePage, buildEventVars(eventConfig, leadFirstName));

  // Arriving from the registration form's redirect, start at the top so the
  // seat-saved line is the first thing on screen.
  useEffect(() => { window.scrollTo(0, 0); }, []);

  if (!copy) return null;
  const first = leadFirstName ? leadFirstName.split(' ')[0] : null;
  const offer = prefillTierCtas(copy.blocks, leadEmail, first);

  return (
    <div className="gate-page upgrade-step" data-testid="upgrade-step">
      <div className="upgrade-step-head">
        <div className="step-two-saved upgrade-step-saved">
          <span className="step-two-check" aria-hidden="true">✓</span> {copy.seatSaved}
        </div>
        <h1 className="upgrade-step-heading">{copy.heading}</h1>
        {copy.body && <p className="upgrade-step-body">{copy.body}</p>}
      </div>
      {offer.length > 0 && (
        <div className="gate-page-upgrade upgrade-step-offer">
          <Blocks blocks={offer} />
        </div>
      )}
      <div className="upgrade-step-decline">
        <button type="button" className="btn btn-secondary btn-xl" data-testid="upgrade-decline" onClick={onDecline}>
          {copy.declineLabel}
        </button>
        {copy.footnote && <div className="upgrade-step-footnote">{copy.footnote}</div>}
      </div>
    </div>
  );
}
